import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Bot, User, Minimize2, Maximize2 } from 'lucide-react';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

export function AIChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: '您好！我是越亘科技的AI智能客服小越，很高兴为您服务。请问有什么可以帮助您的？',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // FAQ知识库
  const faqDatabase = [
    {
      keywords: ['你好', '您好', 'hello', 'hi', '嗨'],
      response: '您好！很高兴为您服务。我可以为您介绍我们的业务、技术优势、成功案例等信息，请问您想了解什么？',
    },
    {
      keywords: ['业务', '服务', '做什么', '主营'],
      response: '越亘科技主要提供三大核心业务：\n\n1. 🚚 跨境物流解决方案 - 国际快递、供应链、仓储服务\n2. 🛒 电子商务生态 - B2B/B2C/O2O全渠道电商平台\n3. ❤️ 智慧健康管理 - 慢病管理、数字化健康服务\n\n您对哪个方向比较感兴趣呢？',
    },
    {
      keywords: ['物流', '跨境', '仓储', '快递', '供应链'],
      response: '我们的跨境物流解决方案包括：\n\n✅ 国际快递/供应链/仓储/转运全链路服务\n✅ 智能分拣系统、Dragon开放接口平台\n✅ 海外仓数字化管理解决方案\n\n已为UPS中国、固德物流等知名企业提供服务。需要我安排专业顾问为您详细介绍吗？',
    },
    {
      keywords: ['电商', '商城', '店铺', '网店', 'b2b', 'b2c'],
      response: '我们的电子商务解决方案包括：\n\n✅ B2B/B2C/O2O全渠道电商平台\n✅ 智慧商城+会员营销+智能客服系统\n✅ 跨境电商EShop解决方案\n\n支持多平台运营，已服务500+企业客户。您需要哪种电商模式呢？',
    },
    {
      keywords: ['健康', '医疗', '慢病', '中医'],
      response: '我们的智慧健康管理服务包括：\n\n✅ 慢病/肥胖/高血压数字化管理方案\n✅ 中医健康管理服务体系\n✅ AI驱动的健康数据分析平台\n\n让科技赋能健康管理，提升用户体验。',
    },
    {
      keywords: ['技术', '平台', '系统', '开发', '双中台'],
      response: '越亘科技拥有强大的技术实力：\n\n🔷 双中台架构：数据中台 + 业务中台\n🔷 技术栈：JAVA、.NET、SaaS、AI、大数据\n🔷 核心产品：数字化供应链、智慧物流、AI决策中台、全渠道电商平台\n\n我们是国家级高新技术企业，拥有完整的技术认证体系。',
    },
    {
      keywords: ['价格', '费用', '多少钱', '报价', '成本'],
      response: '我们的服务费用根据具体需求定制，包括：\n\n• 项目规模和复杂度\n• 功能模块选择\n• 技术架构要求\n• 服务周期和支持\n\n为了给您准确的报价，建议安排专业顾问了解您的具体需求。请留下您的联系方式，我们将尽快与您联系。',
    },
    {
      keywords: ['案例', '客户', '合作', '服务过'],
      response: '我们已服务500+企业客户，包括：\n\n🏢 中国铁建、UPS中国、中粮地产、中免集团\n🏢 珠江船务、斑马物联网、固德物流、嘉谷万邑\n\n涵盖物流、电商、地产、零售等多个行业。99%的客户续约率证明了我们的服务品质！',
    },
    {
      keywords: ['联系', '咨询', '电话', '地址', '邮箱'],
      response: '📞 联系方式：\n\n• 服务热线：+86(023)68141839\n• 电子邮箱：contact@yuegeen.com\n• 重庆总部：重庆市沙坪坝区土主镇永泰路18号华科蔚来云著9栋7-7\n• 官方网站：www.yuegeen.com\n\n我们提供7×24小时响应机制，随时为您服务！',
    },
    {
      keywords: ['优势', '为什么选择', '特点', '强项'],
      response: '选择越亘科技的四大理由：\n\n⚡ 技术创新驱动 - JAVA/.NET/SaaS三大技术平台\n🔗 全链服务能力 - 咨询+规划+产品+实施一体化\n🏆 行业标杆案例 - 服务500强企业经验丰富\n🌏 全国布局网络 - 1+10城市战略快速响应\n\n10年行业经验，值得信赖！',
    },
    {
      keywords: ['人工', '真人', '客服', '转人工'],
      response: '好的，我为您转接人工客服。请稍等片刻，我们的专业顾问会尽快为您服务。\n\n在等待期间，您也可以直接拨打服务热线：+86(023)68141839',
    },
  ];

  const getAIResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();

    // 搜索匹配的FAQ
    for (const faq of faqDatabase) {
      if (faq.keywords.some(keyword => lowerMessage.includes(keyword))) {
        return faq.response;
      }
    }

    // 默认回复
    return `感谢您的咨询！您提到的"${userMessage}"是个很好的问题。\n\n为了给您更专业的��答，建议：\n\n1. 📞 直接拨打服务热线：+86(023)68141839\n2. 📧 发送邮件至：contact@yuegeen.com\n3. 💬 留下联系方式，我们安排专业顾问回电\n\n还有其他问题吗？我可以为您介绍我们的业务、技术、案例等信息。`;
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // 模拟AI思考时间
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: getAIResponse(inputValue),
        sender: 'bot',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // 快捷问题
  const quickQuestions = [
    '了解业务范围',
    '查看成功案例',
    '技术优势介绍',
    '联系方式',
  ];

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setIsOpen(true)}
            data-chat-trigger
            className="fixed bottom-6 right-6 z-50 group"
          >
            <div className="relative">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white shadow-2xl hover:shadow-orange-500/50 transition-all flex items-center justify-center">
                <MessageSquare size={28} className="group-hover:scale-110 transition-transform" />
              </div>
              {/* Notification Badge */}
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">
                1
              </div>
              {/* Pulse Animation */}
              <div className="absolute inset-0 rounded-full bg-orange-500 animate-ping opacity-20"></div>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              height: isMinimized ? 'auto' : undefined 
            }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{ maxHeight: isMinimized ? '60px' : 'calc(100vh - 3rem)', maxWidth: '400px' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                  <Bot size={24} />
                </div>
                <div>
                  <div className="font-semibold">AI智能客服</div>
                  <div className="text-xs text-white/80 flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span>在线</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  {isMinimized ? <Maximize2 size={18} /> : <Minimize2 size={18} />}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`flex items-start space-x-2 max-w-[85%] ${message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          message.sender === 'bot'
                            ? 'bg-gradient-to-br from-orange-500 to-red-500'
                            : 'bg-gray-400'
                        }`}>
                          {message.sender === 'bot' ? (
                            <Bot size={16} className="text-white" />
                          ) : (
                            <User size={16} className="text-white" />
                          )}
                        </div>
                        <div>
                          <div
                            className={`rounded-2xl px-4 py-2 ${
                              message.sender === 'bot'
                                ? 'bg-white border border-gray-200'
                                : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                          </div>
                          <div className="text-xs text-gray-400 mt-1 px-2">
                            {message.timestamp.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {/* Typing Indicator */}
                  {isTyping && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center space-x-2"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                        <Bot size={16} className="text-white" />
                      </div>
                      <div className="bg-white border border-gray-200 rounded-2xl px-4 py-2">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Quick Questions */}
                {messages.length <= 1 && (
                  <div className="px-4 py-3 bg-white border-t border-gray-200">
                    <div className="text-xs text-gray-500 mb-2">快捷问题：</div>
                    <div className="flex flex-wrap gap-2">
                      {quickQuestions.map((question, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setInputValue(question);
                            setTimeout(() => handleSendMessage(), 100);
                          }}
                          className="px-3 py-1.5 text-xs rounded-full bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Input */}
                <div className="p-4 bg-white border-t border-gray-200">
                  <div className="flex items-end space-x-2">
                    <textarea
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="输入您的问题..."
                      rows={1}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none resize-none text-sm"
                      style={{ maxHeight: '100px' }}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim()}
                      className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all"
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}